import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Switch, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

import { TableWrapper } from 'container/styled';
import { CustomTableStyleWrapper } from 'pages/style';
import openNotificationWithIcon from 'utility/notification';
import { CollectionChannelConfig, CollectionActionTypeConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionRuleDto,
  DebtCollectionTemplateDto,
  CollectionChannel,
  CollectionActionType,
} from '../debtCollectionApi';
import RuleFormModal from './RuleFormModal';

interface RulesTabProps {
  configId: number;
}

const RulesTab: React.FC<RulesTabProps> = ({ configId }) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState<DebtCollectionRuleDto[]>([]);
  const [templates, setTemplates] = useState<DebtCollectionTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    visible: boolean;
    editMode: boolean;
    selectedRule: DebtCollectionRuleDto | null;
  }>({ visible: false, editMode: false, selectedRule: null });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, templatesRes] = await Promise.all([
        debtCollectionApi.getRules(),
        debtCollectionApi.getTemplates(),
      ]);
      setRules(rulesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleDelete = async (id: number) => {
    try {
      await debtCollectionApi.deleteRule(id);
      openNotificationWithIcon('success', t('debt-collection:rules.deleted', 'Rule deleted'));
      fetchRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const reordered = Array.from(rules);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setRules(reordered);

    try {
      await debtCollectionApi.reorderRules(reordered.map((r) => r.id!));
    } catch (error) {
      console.error('Error reordering rules:', error);
      fetchRules();
    }
  };

  const handleModalSuccess = () => {
    setModalState({ visible: false, editMode: false, selectedRule: null });
    fetchRules();
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'stepOrder',
      key: 'stepOrder',
      width: 60,
      render: (_: number, __: DebtCollectionRuleDto, index: number) => index + 1,
    },
    {
      title: t('debt-collection:rules.days-overdue', 'Days Overdue'),
      dataIndex: 'daysOverdue',
      key: 'daysOverdue',
      width: 120,
    },
    {
      title: t('debt-collection:rules.action-type', 'Action'),
      dataIndex: 'actionType',
      key: 'actionType',
      render: (val: CollectionActionType) => {
        const cfg = CollectionActionTypeConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:rules.channel', 'Channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (val: CollectionChannel) => {
        const cfg = CollectionChannelConfig[val];
        return cfg ? <Tag color={cfg.color}>{cfg.title}</Tag> : '-';
      },
    },
    {
      title: t('debt-collection:rules.template', 'Template'),
      dataIndex: 'templateName',
      key: 'templateName',
      render: (val: string | null) => val || '-',
    },
    {
      title: t('debt-collection:rules.active', 'Active'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (val: boolean) => <Switch checked={val} disabled size="small" />,
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'actions',
      width: 120,
      render: (_: unknown, record: DebtCollectionRuleDto) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            className="btn-icon"
            type="primary"
            shape="circle"
            size="small"
            onClick={() =>
              setModalState({ visible: true, editMode: true, selectedRule: record })
            }
          >
            <FeatherIcon icon="edit" size={14} />
          </Button>
          <Popconfirm
            title={t('debt-collection:rules.delete-confirm', 'Delete this rule?')}
            onConfirm={() => handleDelete(record.id!)}
            okText={t('global:yes', 'Yes')}
            cancelText={t('global:no', 'No')}
          >
            <Button className="btn-icon" type="primary" danger shape="circle" size="small">
              <FeatherIcon icon="trash-2" size={14} />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={() =>
            setModalState({ visible: true, editMode: false, selectedRule: null })
          }
        >
          <FeatherIcon icon="plus" size={16} />
          {t('debt-collection:rules.add', 'Add Rule')}
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="rules-table">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <CustomTableStyleWrapper>
                <TableWrapper>
                  <Table
                    loading={loading}
                    dataSource={rules}
                    columns={columns}
                    rowKey={(r) => String(r.id)}
                    pagination={false}
                    components={{
                      body: {
                        row: ({ children, ...restProps }: any) => {
                          const index = rules.findIndex(
                            (r) => String(r.id) === restProps['data-row-key'],
                          );
                          if (index < 0) return <tr {...restProps}>{children}</tr>;
                          return (
                            <Draggable
                              key={String(rules[index].id)}
                              draggableId={String(rules[index].id)}
                              index={index}
                            >
                              {(dragProvided) => (
                                <tr
                                  ref={dragProvided.innerRef}
                                  {...restProps}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                >
                                  {children}
                                </tr>
                              )}
                            </Draggable>
                          );
                        },
                      },
                    }}
                  />
                </TableWrapper>
              </CustomTableStyleWrapper>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <RuleFormModal
        visible={modalState.visible}
        editMode={modalState.editMode}
        rule={modalState.selectedRule}
        templates={templates}
        onCancel={() =>
          setModalState({ visible: false, editMode: false, selectedRule: null })
        }
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default RulesTab;
